import React, { useState } from "react";
import Styles from "../../assets/css/auth/login.module.scss";
import Logo from "../../assets/images/logo/Logo.png";
import { useNavigate } from "react-router-dom";
import OpenEye from "../../assets/images/Auth/visibility.png";
import CloseEye from "../../assets/images/Auth/visibility_off.png";
import { adminLogin } from "../../services/Api";
import { AiOutlineLoading } from 'react-icons/ai';
import { validateForm, loginValidationRules } from "../../utils/validationHandler";
import { responseHandler } from "../../utils/responseHandler";
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from "../../features/message/popUpSlice";
import PopUpMessage from "../../utils/errors/PopUpMessage";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const showPopup = (type, heading, message) => {
    dispatch(popUpBoxTrigger({
      type,
      heading,
      message,
      show: true
    }));
  };

  const getValidationErrorMessage = (errors) => {
    const errorMessages = [];
    if (errors.email) {
      errorMessages.push(`Email: ${errors.email}`);
    }
    if (errors.password) {
      errorMessages.push(`Password: ${errors.password}`);
    }
    return errorMessages.join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrors({});

    const { isValid, errors: validationErrors } = validateForm(formData, loginValidationRules);

    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await adminLogin(formData);
      const handledResponse = await responseHandler(response);

      if (handledResponse) {
        if (handledResponse.status === 200) {
          showPopup('success', 'Login Successful', 'You have successfully logged in.');
          if (handledResponse.data?.token) {
            localStorage.setItem('token', handledResponse.data.token);
            localStorage.setItem('userData', JSON.stringify(handledResponse.data));
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
          navigate("/stocklevels");
        } else {
          // Handle specific error messages from the API
          if (handledResponse.message?.toLowerCase().includes('email')) {
            setErrors({
              email: 'Email does not exist'
            });
            // showPopup('error', 'Login Failed', 'Email does not exist');
          } else if (handledResponse.message?.toLowerCase().includes('password')) {
            setErrors({
              password: 'Incorrect password'
            });
            // showPopup('error', 'Login Failed', 'Incorrect password');
          } else {
            showPopup('error', 'Login Failed', handledResponse.message || 'Login failed. Please try again.');
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error);

      if (error.response) {
        const errorMessage = error.response.data?.message || '';

        // Check for specific error messages
        if (errorMessage.toLowerCase().includes('email')) {
          setErrors({
            email: 'Email does not exist'
          });
          // showPopup('error', 'Login Failed', 'Email does not exist');
        } else if (errorMessage.toLowerCase().includes('password')) {
          setErrors({
            password: 'Incorrect password'
          });
          // showPopup('error', 'Login Failed', 'Incorrect password');
        } else {
          switch (error.response.status) {
            case 401:
              // Check error message for specific error
              if (error.response.data?.errors?.email) {
                setErrors({
                  email: error.response.data.errors.email
                });
              } else if (error.response.data?.errors?.password) {
                setErrors({
                  password: error.response.data.errors.password
                });
              }
              break;
            case 422:
              if (error.response.data?.errors) {
                setErrors(error.response.data.errors);
              }
              break;
            case 429:
              showPopup('error', 'Too Many Attempts', 'Please try again later');
              break;
            default:
              showPopup('error', 'Login Error', 'An unexpected error occurred. Please try again.');
          }
        }
      } else {
        showPopup('error', 'Login Error', 'Unable to connect to the server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${Styles.container}`}>
      <PopUpMessage />

      <div className={`${Styles.topSection}`}>
        <div className={`${Styles.logo}`}>
          <img src={Logo} alt="Logo" />
        </div>
      </div>

      <div className={`${Styles.loginBox}`}>
        <h2 className={`${Styles.title}`}>Warehouse Management System</h2>
        <p className={`${Styles.subtitle}`}>Please enter your details to log in.</p>

        <form className={`${Styles.form}`} onSubmit={handleSubmit}>
          <div className={`${Styles.inputGroup} ${errors.email ? Styles.error : ''}`}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter Your Email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="email"
              className={errors.email ? Styles.errorInput : ''}
            />
            {errors.email && <span className={Styles.errorMessage}>{errors.email}</span>}
          </div>

          <div className={`${Styles.inputGroup} ${errors.password ? Styles.error : ''}`}>
            <label htmlFor="password">Password</label>
            <div className={`${Styles.passwordContainer}`}>
              <input
                id="password"
                name="password"
                type={passwordVisible ? "text" : "password"}
                placeholder="Enter Your Password"
                value={formData.password}
                onChange={handleInputChange}
                autoComplete="current-password"
                className={errors.password ? Styles.errorInput : ''}
              />
              <img
                src={passwordVisible ? OpenEye : CloseEye}
                alt="Toggle Password Visibility"
                onClick={togglePasswordVisibility}
                className={`${Styles.eyeIcon}`}
              />
            </div>
            {errors.password && <span className={Styles.errorMessage}>{errors.password}</span>}
          </div>

          <div className={`${Styles.forgotPassword}`}>
            <a href="/resetPassword">Forgot password</a>
          </div>

          <button
            type="submit"
            className={`${Styles.loginButton}`}
            disabled={loading}
          >
            {loading ? <AiOutlineLoading className={`${Styles.spinner}`} /> : "Log in"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
